import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getSearch } from "../store/search/selectors.js";
import { getSearchApiMarketingParams } from "../utils/marketing-url-params.js";
import { LOCATION_IATA_CODE_AND_TYPE_SEPARATOR, MULTISTOP_IATA_LIST_SEPARATOR, SEARCH_PAGE_SIZE_WEB_MOBILE, SEARCH_PAGE_SIZE_MIN_WEB_DESKTOP, SEARCH_PAGE_SIZE_MAX_WEB_DESKTOP, PARAM_LIST_SEPARATOR } from "@flights/constants";
import useProfiler from "./useProfiler.js";
import { flights_funnel_sr_page_size_increase_mobile } from "../utils/experiments/funnel/flights_funnel_sr_page_size_increase_mobile.js";
import { flights_funnel_sr_page_size_increase_desktop } from "../utils/experiments/funnel/flights_funnel_sr_page_size_increase_desktop.js";
import { clearSplitBoundsBrandedFareParams } from "../utils/splitBoundsBrandedFareParams.js";
import { removeFlightDealLandingPageTrackingParams } from "../utils/flight-deals.js";
function getLocationCodes(locations) {
    return locations
        .map(({ code, type }) => {
        if (code === "PNH" && type === "CITY") {
            return `KTI${LOCATION_IATA_CODE_AND_TYPE_SEPARATOR}AIRPORT`;
        }
        return [code, type].filter(Boolean).join(LOCATION_IATA_CODE_AND_TYPE_SEPARATOR);
    })
        .join(PARAM_LIST_SEPARATOR);
}
export function getSearchAPIParams(search, query, bounds) {
    const params = new URLSearchParams(query);
    const { searchSegments } = search;
    if (search.type === "MULTISTOP") {
        const fromCodes = searchSegments.map((eachSegment) => getLocationCodes(eachSegment.from));
        const toCodes = searchSegments.map((eachSegment) => getLocationCodes(eachSegment.to));
        params.set("from", fromCodes.join(MULTISTOP_IATA_LIST_SEPARATOR));
        params.set("to", toCodes.join(MULTISTOP_IATA_LIST_SEPARATOR));
    }
    else {
        const tripSegment = searchSegments[0];
        params.set("from", getLocationCodes(tripSegment.from));
        params.set("to", getLocationCodes(tripSegment.to));
    }
    const marketingParams = getSearchApiMarketingParams();
    for (const marketingParam in marketingParams) {
        params.set(marketingParam, marketingParams[marketingParam]);
    }
    params.set("enableVI", "1");
    params.delete("baseOfferToken");
    params.delete("flexibleTicket");
    params.delete("cancelForAnyReason");
    params.delete("brandedFareIndex");
    removeFlightDealLandingPageTrackingParams(params);
    const isInboundBrandedFareSearch = !!bounds?.inboundFor && params.has("outboundBrandedFareIdx");
    if (!isInboundBrandedFareSearch) {
        clearSplitBoundsBrandedFareParams(params);
    }
    if (bounds?.outboundOnly) {
        params.delete("inboundFor");
        params.set("outboundOnly", "1");
        clearSplitBoundsBrandedFareParams(params);
    }
    if (bounds?.inboundFor) {
        params.delete("outboundOnly");
        params.set("inboundFor", bounds.inboundFor);
    }
    return params;
}
export function useSearchAPIParams(bounds) {
    const search = useSelector(getSearch);
    const history = useHistory();
    const { isDesktop, isMobile } = useProfiler();
    const limit = useMemo(() => {
        if (isDesktop() && !!flights_funnel_sr_page_size_increase_desktop.trackWithDefaultStage()) {
            return flights_funnel_sr_page_size_increase_desktop.trackWithDefaultStage() === 1
                ? SEARCH_PAGE_SIZE_MIN_WEB_DESKTOP
                : SEARCH_PAGE_SIZE_MAX_WEB_DESKTOP;
        }
        if (isMobile() && !!flights_funnel_sr_page_size_increase_mobile.trackWithDefaultStage()) {
            return SEARCH_PAGE_SIZE_WEB_MOBILE;
        }
        return undefined;
    }, [isDesktop, isMobile]);
    return useMemo(() => {
        const params = getSearchAPIParams(search, history.location.search, bounds);
        if (limit && !params.has("limit"))
            params.append("limit", limit.toString());
        return params;
    }, [history.location, limit]);
}
